import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, ExternalLink } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="space-y-6" data-testid="privacy-policy-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
          <Shield className="h-7 w-7 text-amber-400" />
          Privacy Policy
        </h1>
        <p className="text-slate-400 mt-1 text-sm">How we collect, use, and protect your personal data</p>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">1. Data Controller</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p><strong className="text-slate-300">Operator:</strong> Seafarer Logbook</p>
          <p><strong className="text-slate-300">Contact:</strong> <a href="mailto:admin@seafarerlogbook.co.uk" className="text-amber-400 hover:text-amber-300">admin@seafarerlogbook.co.uk</a></p>
          <p>For any data-protection enquiries — including requests to access, correct, export, or delete your personal data — please email the address above. We aim to respond within 30 days as required by UK GDPR.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">2. What Personal Data We Collect</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-3">
          <p><strong className="text-slate-300">Account information:</strong> Name, email address, and hashed password when you register.</p>
          <p><strong className="text-slate-300">Trip and earnings data:</strong> Dates, vessel names, contractors, earnings figures, and expense records you enter into the app.</p>
          <p><strong className="text-slate-300">Documents:</strong> Wage slips and other files you upload for AI-assisted parsing.</p>
          <p><strong className="text-slate-300">Payment data:</strong> Stripe processes your payment card details. We store only transaction references, amounts, and dates — never your full card number.</p>
          <p><strong className="text-slate-300">Usage analytics:</strong> With your consent, we collect anonymised usage data (pages visited, feature usage) via PostHog to improve the app. No analytics are collected without consent.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">3. Why We Process Your Data (Legal Basis)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-3">
          <p><strong className="text-slate-300">Contract performance:</strong> We process your account, trip, and earnings data to provide the service you signed up for — tracking your Seafarer's Earnings Deduction eligibility.</p>
          <p><strong className="text-slate-300">Consent:</strong> Analytics cookies and marketing emails (if any) are only used with your explicit consent, which you can withdraw at any time.</p>
          <p><strong className="text-slate-300">Legitimate interest:</strong> We may process limited data to prevent fraud, enforce our terms, and improve app security.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">4. Third-Party Services (Sub-Processors)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-3">
          <p>We share your data with the following third parties only as necessary to provide the service:</p>
          <div className="space-y-2 ml-4">
            <p><strong className="text-slate-300">Stripe</strong> — Payment processing. Stripe handles your card details under their own privacy policy. <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">Stripe Privacy <ExternalLink className="h-3 w-3" /></a></p>
            <p><strong className="text-slate-300">Resend</strong> — Transactional email delivery (password resets, invitations, reports). <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">Resend Privacy <ExternalLink className="h-3 w-3" /></a></p>
            <p><strong className="text-slate-300">Google Gemini (via Emergent)</strong> — AI-powered wage slip text extraction. Uploaded document text is sent for parsing only; no data is retained by the AI service after processing.</p>
            <p><strong className="text-slate-300">PostHog</strong> — Anonymised usage analytics (only with your consent). <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">PostHog Privacy <ExternalLink className="h-3 w-3" /></a></p>
            <p><strong className="text-slate-300">MongoDB Atlas</strong> — Database hosting for all your account and application data.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">5. Data Retention</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>Your data is retained for as long as your account is active. If you delete your account, all personal data — including trips, documents, earnings records, expenses, and referral history — is permanently deleted within 30 days.</p>
          <p>Payment transaction records may be retained for up to 7 years to comply with UK financial record-keeping requirements.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">6. Your Rights Under UK GDPR</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-3">
          <p>Under the UK General Data Protection Regulation, you have the following rights:</p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li><strong className="text-slate-300">Right of access</strong> — Request a copy of the personal data we hold about you.</li>
            <li><strong className="text-slate-300">Right to rectification</strong> — Correct inaccurate data via the app or by contacting us.</li>
            <li><strong className="text-slate-300">Right to erasure</strong> — Delete your account and all associated data from the Legal page in Settings.</li>
            <li><strong className="text-slate-300">Right to data portability</strong> — Export your data via the Reports page (Excel, CSV, or PDF).</li>
            <li><strong className="text-slate-300">Right to restrict processing</strong> — Request that we limit how we use your data.</li>
            <li><strong className="text-slate-300">Right to object</strong> — Object to processing based on legitimate interest.</li>
            <li><strong className="text-slate-300">Right to withdraw consent</strong> — Withdraw analytics consent at any time via the cookie settings.</li>
          </ul>
          <p className="mt-2">To exercise any of these rights, use the in-app controls or contact us by email. We will respond within 30 days.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">7. Cookies & Analytics</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p><strong className="text-slate-300">Essential cookies:</strong> Required for authentication and session management. These cannot be disabled.</p>
          <p><strong className="text-slate-300">Analytics cookies (PostHog):</strong> Used to understand how the app is used, to identify bugs, and to improve features. These are only activated with your explicit consent and can be disabled at any time from the cookie consent banner.</p>
          <p>We do not use advertising cookies or share data with advertisers.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">8. Data Security</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>We take reasonable technical and organisational measures to protect your data, including:</p>
          <ul className="list-disc list-inside ml-2 space-y-1">
            <li>Passwords are hashed using bcrypt (never stored in plain text)</li>
            <li>All data transmitted over HTTPS/TLS encryption</li>
            <li>JWT-based authentication with configurable expiry</li>
            <li>Rate limiting on authentication endpoints</li>
          </ul>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">9. International Transfers</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>Some of our third-party providers (Stripe, PostHog, Google) may process data outside the UK. Where this occurs, appropriate safeguards are in place (such as Standard Contractual Clauses or adequacy decisions) to ensure your data is protected to UK GDPR standards.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">10. Changes to This Policy</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>We may update this privacy policy from time to time. Material changes will be communicated via email or an in-app notification. The "last updated" date below will always reflect the most recent revision.</p>
          <p className="text-xs text-slate-500 mt-3">Last updated: April 2026.</p>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base">11. Complaints</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2">
          <p>If you are unhappy with how we handle your data, you have the right to lodge a complaint with the UK Information Commissioner's Office (ICO):</p>
          <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 inline-flex items-center gap-1">
            ICO — Make a Complaint <ExternalLink className="h-3 w-3" />
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivacyPolicy;
